import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  Tabs,
  Tab,
  Icon,
  Divider,
  CircularProgress,
} from "@mui/material";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import ServicesApi from "../../Classes/API/Services/ServicesApi";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import DatePicker from "components/DatePicker/index";

export default function BundleReports() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [negativeData, setNegativeData] = useState([]);
  const [historyData, setHistoryData] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const fetchNegativeReport = async () => {
    setLoading(true);
    try {
      const res = await ServicesApi.GetNegativeBalances();
      if (res.status === "Success") setNegativeData(res.data);
    } catch (err) {
      toast.error("Error loading report");
    } finally {
      setLoading(false);
    }
  };

  const fetchHistoryReport = async () => {
    if (!startDate || !endDate) return toast.error("Select dates first");
    setLoading(true);
    try {
      const formattedStart = dayjs(startDate).format("YYYY-MM-DD");
      const formattedEnd = dayjs(endDate).format("YYYY-MM-DD");
      const res = await ServicesApi.GetBundleHistory(
        formattedStart,
        formattedEnd,
      );
      if (res.status === "Success") setHistoryData(res.data);
    } catch (err) {
      toast.error("Error loading history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 0) fetchNegativeReport();
  }, [tab]);

  const exportToExcel = () => {
    let dataToExport = [];
    let fileName = "";

    if (tab === 0) {
      fileName = "Negative_Balances_Report";
      dataToExport = negativeData.map((item) => ({
        "Patient Name": item.patientName,
        "Bundle ID": item.bundleId,
        "Balance (EGP)": item.balance,
      }));
    } else {
      fileName = `History_Report_${dayjs(startDate).format("DD-MM-YY")}_to_${dayjs(endDate).format("DD-MM-YY")}`;
      historyData.forEach((bundle) => {
        bundle.items.forEach((item) => {
          dataToExport.push({
            "Bundle ID": bundle.bundleId,
            "Patient Name": bundle.patientName,
            Service: item.serviceName,
            Date: dayjs(item.serviceDate).format("DD/MM/YY"),
            Price: item.priceAtTime,
            Status: item.status,
          });
        });
      });
    }

    if (dataToExport.length === 0) return toast.error("No data to export");

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, `${fileName}.xlsx`);
  };

  const negColumns = [
    { Header: "Patient Name", accessor: "patientName" },
    { Header: "Bundle ID", accessor: "bundleId" },
    {
      Header: "Balance",
      accessor: "balance",
      Cell: ({ value }) => (
        <MDTypography variant="button" color="error" fontWeight="bold">
          {value.toLocaleString()} EGP
        </MDTypography>
      ),
    },
    {
      Header: "View",
      Cell: ({ row }) => (
        <MDButton
          variant="text"
          color="info"
          onClick={() =>
            navigate(
              `/ViewServiceBundles/BundleDetails/${row.original.bundleId}`,
            )
          }
        >
          <Icon>visibility</Icon>
        </MDButton>
      ),
    },
  ];

  const histColumns = [
    { Header: "ID", accessor: "bundleId", width: "10%" },
    { Header: "Patient", accessor: "patientName", width: "25%" },
    {
      Header: "Services",
      accessor: "items",
      Cell: ({ value }) => (
        <MDBox>
          {value.map((item, i) => (
            <MDTypography
              key={i}
              variant="caption"
              display="block"
              sx={{ borderBottom: "1px solid #eee" }}
            >
              {item.serviceName} | {dayjs(item.serviceDate).format("DD/MM/YY")}{" "}
              | {item.status}
            </MDTypography>
          ))}
        </MDBox>
      ),
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={3} mb={3}>
        <Card>
          <MDBox p={2} display="flex">
            <Tabs
              sx={{ width: "100%" }}
              value={tab}
              onChange={(e, v) => setTab(v)}
            >
              <Tab
                label="Negative Balances"
                icon={<Icon>money_off</Icon>}
                iconPosition="start"
              />
              <Tab
                label="Historical Report"
                icon={<Icon>history</Icon>}
                iconPosition="start"
              />
            </Tabs>
            <MDButton
              sx={{ width: "20%" }}
              variant="gradient"
              color="success"
              onClick={exportToExcel}
              startIcon={<Icon>download</Icon>}
            >
              Export Excel
            </MDButton>
          </MDBox>
          <Divider sx={{ m: 0 }} />
          <MDBox p={3}>
            {tab === 1 && (
              <Grid container spacing={2} alignItems="center" mb={3}>
                <Grid item xs={12} md={4}>
                  <DatePicker
                    label="Start"
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DatePicker
                    label="End"
                    selected={endDate}
                    onChange={(date) => setEndDate(date)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <MDButton
                    variant="gradient"
                    color="info"
                    fullWidth
                    onClick={fetchHistoryReport}
                  >
                    Search
                  </MDButton>
                </Grid>
              </Grid>
            )}

            {loading ? (
              <MDBox display="flex" justifyContent="center" p={5}>
                <CircularProgress />
              </MDBox>
            ) : (
              <DataTable
                table={{
                  columns: tab === 0 ? negColumns : histColumns,
                  rows: tab === 0 ? negativeData : historyData,
                }}
                isSorted={false}
                entriesPerPage={false}
                showTotalEntries={true}
              />
            )}
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}
